/**
 * Cards board — "4+2 Lifecycle Architecture" view.
 *
 * Faithful render of the architecture diagram (Mapping the Complete
 * Legal Surface Area / The 4+2 Lifecycle Architecture), NOT a clone of
 * the Kaiyang interim `LifecycleRegulationMap` 6-col grid. The two
 * diverge — code is interim, the diagram is the shipping contract.
 * See KAIYANG-LESSONS §25.12 / ADR-0003.
 *
 * Layout (top-down):
 *   ── 4 LIFECYCLE LANES ──
 *      A · Design & Approval
 *      B · Market Entry & Registration
 *      C · Sales Economics
 *      D · In-Use & Operations
 *   ── 2 CROSS-CUTTING MODULES (full-width bands) ──
 *      P · Legal Exposure Coverage
 *      U · Use Case Intelligence
 *   ── 3 AUDITABILITY PANELS ──
 *      Hidden by configuration
 *      Unknown · needs configuration
 *      Placeholder · curation gap
 *
 * Color contract:
 *   hard_law          → --ft-hard-law      (Crimson Red)
 *   evidence_standard → --ft-evidence      (Amber)
 *   market_force      → --ft-market-force  (Cobalt Blue)
 *   ecosystem_signal  → --ft-ecosystem     (Light Gray)
 *   continuing        → --ft-continuing    (Deep Purple) — applied to the
 *                       continuing-obligation chip on any layer
 */

import type { ComplianceRequirementCard, LegalEffect, LifecycleLayer } from "@regpulse/contracts";
import { LegalReferenceBadge } from "./LegalReferenceBadge";
import { ContinuingObligationBadge } from "./ContinuingObligationBadge";

const MAIN_LANES: LifecycleLayer[] = ["A", "B", "C", "D"];
const CROSS_CUTTING: LifecycleLayer[] = ["P", "U"];

const LAYER_TITLE: Record<LifecycleLayer, string> = {
  A: "A · Design & Approval",
  B: "B · Market Entry & Registration",
  C: "C · Sales Economics",
  D: "D · In-Use & Operations",
  P: "P · Legal Exposure Coverage",
  U: "U · Use Case Intelligence",
};

const LAYER_CAPTION: Record<LifecycleLayer, string> = {
  A: "UNECE R-series, Euro 7 Tech, CSMS/SUMS — how the vehicle is built and approved.",
  B: "National approvals, GDPR, AI Act prohibited practices — can it be sold and registered?",
  C: "CO2 fleet targets, local purchase taxes — commercial exposure.",
  D: "FR Code de la route, OTA operational compliance — operational friction.",
  P: "Categorical legal-exposure view across applicable obligations (market access · manufacturer/importer · fines/recalls/surveillance · product & data liability · customs) — advisory effect-type classification only, never a single figure. Product-liability cards are listed below; specific instruments are carried as registry data on each card.",
  U: "Use-case / scenario intelligence mapped to regulation clusters + key issues — shown in the Use Case Intelligence catalog below (ADR-0008). Cross-cutting; advisory, not a legal determination.",
};

const LEGAL_EFFECT_ORDER: LegalEffect[] = [
  "market_access_blocker",
  "registration_blocker",
  "in_use_restriction",
  "cost_penalty",
  "rating_penalty",
  "commercial_risk",
  "information_only",
];
const LEGAL_EFFECT_LABEL: Record<LegalEffect, string> = {
  market_access_blocker: "Market-access blocker",
  registration_blocker: "Registration blocker",
  in_use_restriction: "In-use restriction",
  cost_penalty: "Cost penalty",
  rating_penalty: "Rating penalty",
  commercial_risk: "Commercial risk",
  information_only: "Information only",
};

const KIND_LABEL: Record<ComplianceRequirementCard["obligationKind"], string> = {
  hard_law: "Hard law",
  evidence_standard: "Evidence",
  market_force: "Non-legal · Market force",
  ecosystem_signal: "Non-legal · Ecosystem",
};

const KIND_COLOR: Record<ComplianceRequirementCard["obligationKind"], { rail: string; chipBg: string; chipText: string }> = {
  hard_law:          { rail: "var(--ft-hard-law)",     chipBg: "var(--ft-hard-law-soft)",     chipText: "var(--ft-hard-law)" },
  evidence_standard: { rail: "var(--ft-evidence)",     chipBg: "var(--ft-evidence-soft)",     chipText: "var(--ft-evidence)" },
  market_force:      { rail: "var(--ft-market-force)", chipBg: "var(--ft-market-force-soft)", chipText: "var(--ft-market-force)" },
  ecosystem_signal:  { rail: "var(--ft-ecosystem)",    chipBg: "var(--ft-ecosystem-soft)",    chipText: "var(--ft-ecosystem)" },
};

interface Props {
  cards: ComplianceRequirementCard[];
  outcomes: Array<{ ruleId: string; outcome: { category: string; reason?: string } }>;
  onSelectCard?: (cardId: string) => void;
  selectedCardId?: string | null;
  /** When set (a lens is active), cards NOT in this set are dimmed in the
   *  lanes/bands — a highlight overlay, never a filter (ADR-0007). */
  matchedCardIds?: Set<string> | null;
}

export function CardsBoard({ cards, outcomes, onSelectCard, selectedCardId, matchedCardIds }: Props) {
  const outcomeById = new Map(outcomes.map((o) => [o.ruleId, o.outcome]));

  const applicableByLayer = new Map<LifecycleLayer, ComplianceRequirementCard[]>();
  for (const layer of [...MAIN_LANES, ...CROSS_CUTTING]) applicableByLayer.set(layer, []);
  const hidden: ComplianceRequirementCard[] = [];
  const unknown: ComplianceRequirementCard[] = [];
  const placeholders: ComplianceRequirementCard[] = [];

  for (const card of cards) {
    if (card.lifecycleState === "PLACEHOLDER") {
      placeholders.push(card);
      continue;
    }
    switch (card.applicabilityCategory) {
      case "applicable":
        applicableByLayer.get(card.layer)?.push(card);
        break;
      case "hidden_by_config":
        hidden.push(card);
        break;
      case "unknown_needs_config":
        unknown.push(card);
        break;
    }
  }

  const totalApplicable = Array.from(applicableByLayer.values()).reduce(
    (n, arr) => n + arr.length,
    0,
  );


  // Categorical legalEffect distribution across applicable cards for the P band
  // (ADR-0008). Cross-layer by design: counts ALL applicable obligations
  // (A/B/C/D/P), not only P-band cards. Advisory per-effect counts — never an
  // aggregate figure.
  const legalEffectDistribution = LEGAL_EFFECT_ORDER.map((effect) => ({
    effect,
    count: cards.filter(
      (c) => c.applicabilityCategory === "applicable" && c.legalEffect === effect,
    ).length,
  })).filter((e) => e.count > 0);

  return (
    <div style={{ display: "grid", gap: "var(--space-24)" }} data-testid="cards-board">
      <section aria-labelledby="lifecycle-lanes-title">
        <header style={{ marginBottom: "var(--space-12)" }}>
          <h2 id="lifecycle-lanes-title" className="type-h2">
            Lifecycle layers — visible applicable · {totalApplicable}
          </h2>
          <p className="type-caption" style={{ margin: 0 }}>
            <strong>4+2 architecture.</strong> Top: four lifecycle lanes (A/B/C/D).
            Below: two horizontal cross-cutting modules (P · Legal Exposure,
            U · Use Case Intelligence). Hidden / unknown / placeholder cards
            live in the auditability panels at the bottom — never silently dropped.
          </p>
          <p className="type-caption" style={{ margin: "var(--space-4) 0 0 0" }}>
            This visible count excludes placeholder / curation-gap cards, which are shown in the dedicated placeholder panel.
          </p>
        </header>

        {/* ── 4 main lanes ─────────────────────────────────────────── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(220px, 1fr))",
            gap: "var(--space-12)",
            overflowX: "auto",
          }}
          data-testid="lifecycle-lanes"
        >
          {MAIN_LANES.map((layer) => (
            <Lane
              key={layer}
              layer={layer}
              cards={applicableByLayer.get(layer) ?? []}
              outcomeById={outcomeById}
              onSelectCard={onSelectCard}
              selectedCardId={selectedCardId}
              matchedCardIds={matchedCardIds}
            />
          ))}
        </div>

        {/* ── 2 cross-cutting modules (full-width bands) ───────────── */}
        <div style={{ marginTop: "var(--space-16)", display: "grid", gap: "var(--space-12)" }}>
          {CROSS_CUTTING.map((layer) => (
            <CrossCuttingBand
              key={layer}
              layer={layer}
              cards={applicableByLayer.get(layer) ?? []}
              outcomeById={outcomeById}
              onSelectCard={onSelectCard}
              selectedCardId={selectedCardId}
              matchedCardIds={matchedCardIds}
              exposureSummary={layer === "P" ? legalEffectDistribution : undefined}
              note={
                layer === "U" ? (
                  <>
                    Use-case intelligence is shown in the Use Case Intelligence catalog
                    below (ADR-0008) — real-world use-cases mapped to regulation clusters,
                    labelled by coverage status.
                  </>
                ) : undefined
              }
            />
          ))}
        </div>
      </section>

      <ParallelPanel
        id="hidden_by_config"
        title="Hidden by configuration"
        caption="Excluded by an explicit configuration choice. Listed for auditability — never silently dropped."
        cards={hidden}
        outcomeById={outcomeById}
        tint="var(--color-surface-sunken)"
        onSelectCard={onSelectCard}
        selectedCardId={selectedCardId}
      />
      <ParallelPanel
        id="unknown_needs_config"
        title="Unknown · needs configuration"
        caption="Trigger references a config field set to null. Resolve the field to determine applicability."
        cards={unknown}
        outcomeById={outcomeById}
        tint="var(--status-unknown-config-soft)"
        onSelectCard={onSelectCard}
        selectedCardId={selectedCardId}
      />
      <ParallelPanel
        id="placeholder_curation_gap"
        title="Placeholder · curation gap"
        caption="Rules whose canonical text is not yet ingested. Verification tier SEED_UNVERIFIED."
        cards={placeholders}
        outcomeById={outcomeById}
        tint="var(--status-candidate-pending-soft)"
        onSelectCard={onSelectCard}
        selectedCardId={selectedCardId}
      />
    </div>
  );
}

function Lane({
  layer,
  cards,
  outcomeById,
  onSelectCard,
  selectedCardId,
  matchedCardIds,
}: {
  layer: LifecycleLayer;
  cards: ComplianceRequirementCard[];
  outcomeById: Map<string, { category: string; reason?: string }>;
  onSelectCard?: (id: string) => void;
  selectedCardId?: string | null;
  matchedCardIds?: Set<string> | null;
}) {
  return (
    <section
      aria-label={LAYER_TITLE[layer]}
      data-layer={layer}
      data-testid={`lane-${layer}`}
      style={{ display: "flex", flexDirection: "column", gap: "var(--space-8)", minWidth: 0 }}
    >
      <header
        style={{
          position: "sticky",
          top: 0,
          background: "var(--color-surface-sunken)",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius-4)",
          padding: "var(--space-8)",
          zIndex: 1,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
          {LAYER_TITLE[layer]}{" "}
          <span style={{ color: "var(--color-text-secondary)" }}>({cards.length})</span>
        </div>
        <div style={{ fontSize: 10, color: "var(--color-text-secondary)", marginTop: 2, lineHeight: 1.35 }}>
          {LAYER_CAPTION[layer]}
        </div>
      </header>
      {cards.length === 0 ? (
        <EmptyLane label={layer} />
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: "var(--space-8)" }}>
          {cards.map((card) => (
            <li key={card.id}>
              <Card
                card={card}
                outcomeReason={outcomeById.get(card.id)?.reason}
                compact
                matchedCardIds={matchedCardIds}
                onSelect={onSelectCard}
                selected={selectedCardId === card.id}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CrossCuttingBand({
  layer,
  cards,
  outcomeById,
  onSelectCard,
  selectedCardId,
  countLabel,
  note,
  exposureSummary,
  matchedCardIds,
}: {
  layer: LifecycleLayer;
  cards: ComplianceRequirementCard[];
  outcomeById: Map<string, { category: string; reason?: string }>;
  onSelectCard?: (id: string) => void;
  selectedCardId?: string | null;
  countLabel?: string;
  note?: React.ReactNode;
  matchedCardIds?: Set<string> | null;
  exposureSummary?: { effect: LegalEffect; count: number }[];
}) {
  return (
    <section
      aria-label={LAYER_TITLE[layer]}
      data-layer={layer}
      data-testid={`band-${layer}`}
      data-cross-cutting="true"
      style={{
        background: "var(--color-surface-sunken)",
        border: "1px dashed var(--color-border-strong)",
        borderRadius: "var(--radius-8)",
        padding: "var(--space-12)",
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: "var(--space-12)",
          marginBottom: "var(--space-8)",
        }}
      >
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {LAYER_TITLE[layer]}{" "}
            <span style={{ color: "var(--color-text-secondary)" }}>{countLabel ?? `(${cards.length})`}</span>
          </div>
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2, lineHeight: 1.4 }}>
            {LAYER_CAPTION[layer]}
          </div>
        </div>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            padding: "1px var(--space-8)",
            borderRadius: "var(--radius-full)",
            border: "1px dashed var(--color-border-strong)",
            color: "var(--color-text-secondary)",
          }}
        >
          horizontal · cross-cutting
        </span>
      </header>
      {exposureSummary && exposureSummary.length > 0 && (
        <div data-testid="p-exposure-summary" style={{ marginBottom: "var(--space-8)" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-4)" }}>
            {exposureSummary.map(({ effect, count }) => (
              <span
                key={effect}
                data-testid={`p-exposure-effect-${effect}`}
                data-count={count}
                className="type-mono"
                style={{
                  fontSize: 10,
                  padding: "0 var(--space-4)",
                  borderRadius: "var(--radius-4)",
                  border: "1px solid var(--color-border)",
                  color: "var(--color-text-secondary)",
                }}
              >
                {LEGAL_EFFECT_LABEL[effect]} · {count}
              </span>
            ))}
          </div>
          <p
            className="type-caption"
            style={{ margin: "var(--space-4) 0 0 0", color: "var(--color-text-disabled)", fontStyle: "italic" }}
          >
            Advisory effect-type classification across applicable obligations — categorical
            per-effect counts, not a legal determination (ADR-0002).
          </p>
        </div>
      )}
      {cards.length === 0 ? (
        <p
          style={{
            margin: 0,
            fontSize: 11,
            color: "var(--color-text-secondary)",
            fontStyle: "italic",
          }}
        >
          {note ??
            (layer === "P"
              ? "No PLD / product-liability exposures triggered by this configuration yet."
              : "Use-case intelligence is shown in the Use Case Intelligence catalog below (ADR-0008).")}
        </p>
      ) : (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "var(--space-8)",
          }}
        >
          {cards.map((card) => (
            <li key={card.id}>
              <Card
                card={card}
                outcomeReason={outcomeById.get(card.id)?.reason}
                compact
                matchedCardIds={matchedCardIds}
                onSelect={onSelectCard}
                selected={selectedCardId === card.id}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function EmptyLane({ label }: { label: LifecycleLayer }) {
  return (
    <div
      style={{
        flex: 1,
        minHeight: 80,
        border: "1px dashed var(--color-border)",
        borderRadius: "var(--radius-4)",
        padding: "var(--space-8)",
        fontSize: 11,
        color: "var(--color-text-secondary)",
        fontStyle: "italic",
      }}
    >
      No active {label}-layer requirements for this configuration.
    </div>
  );
}

interface ParallelPanelProps {
  id: string;
  title: string;
  caption: string;
  cards: ComplianceRequirementCard[];
  outcomeById: Map<string, { category: string; reason?: string }>;
  tint: string;
  onSelectCard?: (id: string) => void;
  selectedCardId?: string | null;
}

function ParallelPanel({
  id,
  title,
  caption,
  cards,
  outcomeById,
  tint,
  onSelectCard,
  selectedCardId,
}: ParallelPanelProps) {
  if (cards.length === 0) return null;
  return (
    <section aria-labelledby={`panel-${id}`} data-testid={`panel-${id}`}>
      <header style={{ marginBottom: "var(--space-8)" }}>
        <h2 id={`panel-${id}`} className="type-h2">
          {title} · {cards.length}
        </h2>
        <p className="type-caption" style={{ margin: 0 }}>
          {caption}
        </p>
      </header>
      <ul
        style={{
          listStyle: "none",
          margin: 0,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
          gap: "var(--space-12)",
          background: tint,
          padding: "var(--space-8)",
          borderRadius: "var(--radius-8)",
        }}
      >
        {cards.map((card) => (
          <li key={card.id}>
            <Card
              card={card}
              outcomeReason={outcomeById.get(card.id)?.reason}
              onSelect={onSelectCard}
              selected={selectedCardId === card.id}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function Card({
  card,
  outcomeReason,
  compact = false,
  onSelect,
  selected = false,
  matchedCardIds,
}: {
  card: ComplianceRequirementCard;
  outcomeReason?: string;
  compact?: boolean;
  onSelect?: (id: string) => void;
  selected?: boolean;
  matchedCardIds?: Set<string> | null;
}) {
  const palette = KIND_COLOR[card.obligationKind];
  const clickable = Boolean(onSelect);
  const dimmed = matchedCardIds != null && !matchedCardIds.has(card.id);
  return (
    <article
      style={{
        padding: compact ? "var(--space-8)" : "var(--space-16)",
        background: "var(--color-surface-elevated)",
        border: selected ? `1px solid ${palette.rail}` : "1px solid var(--color-border)",
        borderLeft: `4px solid ${palette.rail}`,
        borderRadius: "var(--radius-8)",
        display: "grid",
        gap: compact ? "var(--space-4)" : "var(--space-12)",
        fontSize: compact ? 11 : undefined,
        opacity: dimmed ? 0.3 : 1,
        filter: dimmed ? "grayscale(0.55)" : undefined,
        transition: "opacity var(--duration-fast) var(--easing-standard)",
        cursor: clickable ? "pointer" : "default",
        outline: selected ? "2px solid var(--color-focus-ring)" : "none",
        outlineOffset: 1,
      }}
      data-card-id={card.id}
      data-dimmed={dimmed ? "true" : "false"}
      data-kind={card.obligationKind}
      data-selected={selected ? "true" : "false"}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? `Open requirement inspector for ${card.title}` : undefined}
      onClick={clickable ? () => onSelect!(card.id) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect!(card.id);
              }
            }
          : undefined
      }
    >
      <header style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-4)" }}>
        <div style={{ minWidth: 0 }}>
          <span className="type-mono" style={{ color: "var(--color-text-secondary)", fontSize: 10 }}>
            {card.id}
          </span>
          <h3
            className="type-h3"
            style={{ marginTop: 2, fontSize: compact ? 12 : undefined, lineHeight: 1.3 }}
          >
            {card.title}
          </h3>
        </div>
        <span
          data-chip="obligation-kind"
          style={{
            alignSelf: "flex-start",
            fontSize: 10,
            fontWeight: 700,
            padding: "1px var(--space-4)",
            borderRadius: "var(--radius-4)",
            background: palette.chipBg,
            color: palette.chipText,
            border: `1px solid ${palette.rail}`,
            whiteSpace: "nowrap",
          }}
        >
          {KIND_LABEL[card.obligationKind]}
        </span>
      </header>

      {card.provenanceTier === "locally_promoted" && (
        <span
          data-testid="provenance-locally-promoted"
          title="Materialised from a reviewer-promoted discovery signal — not part of the declared-scope baseline."
          style={{
            alignSelf: "flex-start",
            fontSize: 9,
            fontWeight: 700,
            padding: "1px var(--space-4)",
            borderRadius: "var(--radius-4)",
            border: "1px dashed var(--color-border-strong)",
            color: "var(--color-text-secondary)",
            whiteSpace: "nowrap",
          }}
        >
          <span aria-hidden="true">⬆ </span>Locally promoted · not declared-scope
        </span>
      )}

      {!compact && (
        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "var(--space-4) var(--space-12)",
            margin: 0,
            fontSize: 12,
          }}
        >
          <dt style={{ color: "var(--color-text-secondary)" }}>Domain</dt>
          <dd style={{ margin: 0 }}>{card.regulatoryDomain}</dd>
          <dt style={{ color: "var(--color-text-secondary)" }}>Timing</dt>
          <dd style={{ margin: 0 }}>{card.obligationTiming}</dd>
          <dt style={{ color: "var(--color-text-secondary)" }}>Layer</dt>
          <dd style={{ margin: 0 }}>
            <span className="type-mono">{card.layer}</span> · {card.lifecycleState}
          </dd>
          <dt style={{ color: "var(--color-text-secondary)" }}>Maturity</dt>
          <dd style={{ margin: 0 }}>
            <span className="type-mono">{card.verificationMaturityTier}</span>
          </dd>
        </dl>
      )}

      {compact && (
        <div
          style={{
            display: "flex",
            gap: "var(--space-4)",
            flexWrap: "wrap",
            fontSize: 10,
            color: "var(--color-text-secondary)",
          }}
        >
          <span>{card.regulatoryDomain}</span>
          <span>·</span>
          <span>{card.obligationTiming}</span>
          <span>·</span>
          <span className="type-mono">{card.verificationMaturityTier}</span>
        </div>
      )}

      {card.legalReferences.length > 0 && !compact && (
        <div>
          <p className="type-caption" style={{ margin: "0 0 var(--space-4) 0" }}>
            Legal references
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-4)" }}>
            {card.legalReferences.slice(0, 4).map((r, i) => (
              <LegalReferenceBadge key={`${r.instrument}-${i}`} reference={r} />
            ))}
            {card.legalReferences.length > 4 && (
              <span className="type-caption">+{card.legalReferences.length - 4} more</span>
            )}
          </div>
        </div>
      )}

      {card.continuingObligation && (
        <div
          data-chip="continuing-obligation"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: compact ? 10 : 11,
            padding: "1px var(--space-8)",
            borderRadius: "var(--radius-full)",
            background: "var(--ft-continuing-soft)",
            color: "var(--ft-continuing)",
            border: "1px solid var(--ft-continuing)",
            alignSelf: "flex-start",
          }}
          title={`${card.continuingObligation.triggerOrFrequency} · ${card.continuingObligation.legalBasis}`}
        >
          ◆ Continuing · {card.continuingObligation.triggerOrFrequency}
        </div>
      )}

      {!compact && card.continuingObligation && (
        <ContinuingObligationBadge obligation={card.continuingObligation} />
      )}

      {card.applicabilityCategory === "unknown_needs_config" &&
        card.needsConfigFields &&
        card.needsConfigFields.length > 0 && (
          <div
            style={{
              padding: "var(--space-4) var(--space-8)",
              background: "var(--status-unknown-config-soft)",
              border: "1px dashed var(--status-unknown-config)",
              borderRadius: "var(--radius-4)",
              fontSize: 11,
            }}
          >
            <strong>Needs: </strong>
            {card.needsConfigFields.map((f, i) => (
              <span key={f} className="type-mono">
                {f}
                {i < card.needsConfigFields!.length - 1 ? ", " : ""}
              </span>
            ))}
          </div>
        )}

      {outcomeReason && !compact && (
        <p className="type-caption" style={{ margin: 0, fontStyle: "italic" }}>
          {outcomeReason}
        </p>
      )}
    </article>
  );
}
